<script setup lang="ts">
import type { NotificationInfo } from '~/composables/notification'
import { onMounted } from 'vue';

const { 
  notifications,
  fetchNotifications, 
  markAsRead,
  getNotificationStyle,
  loading,
  error
} = useNotifications()
const { visited } = useNotifications() // Keep legacy for now
const { t } = useI18n()
const { formatRelativeTime } = useLocale()

// Use the centralized formatTimeAgo function
const formatTimeAgo = (date: Date) => formatRelativeTime(date)

// Fetch notifications on mount
onMounted(async () => {
  await refreshNotifications()
})

async function refreshNotifications() {
  try {
    await fetchNotifications({ per_page: 50 }) // Fetch up to 50 recent notifications
  } catch (error) {
    console.error('Failed to fetch notifications:', error)
  }
}
const { isNotificationsSlideoverOpen } = useDashboard()

// Watch for slideover open/close to refresh notifications
watch(isNotificationsSlideoverOpen, async (isOpen) => {
  if (isOpen) {
    // Refresh notifications when slideover opens
    await refreshNotifications()
  }
})

function get_link(notification: NotificationInfo) {
  const dayParam = notification.day || notification.shift_date || 0
  const taskParam = notification.task_id || 0
  const userParam = notification.user_id || 0
  
  if (notification.type == 'TimeChanged') {
    // For pending approvals, include user_id for pre-selection but not filtering
    const params = new URLSearchParams()
    if (dayParam) params.append('day_id', dayParam.toString())
    if (userParam) params.append('user_id', userParam.toString()) // For pre-selecting employee
    if (taskParam) params.append('task_id', taskParam.toString())
    return `/dashboard/pending_approvals?${params.toString()}`
  } else if (notification.type == 'TimeApproved' || notification.type == 'TimeUnapproved') {
    const params = new URLSearchParams()
    if (dayParam) params.append('day_id', dayParam.toString())
    if (taskParam) params.append('task_id', taskParam.toString())
    return `/dashboard/my_shifts?${params.toString()}`
  } else if (notification.type == 'CommentChanged') {
    const params = new URLSearchParams()
    if (dayParam) params.append('day_id', dayParam.toString())
    if (taskParam) params.append('task_id', taskParam.toString())
    return `/dashboard/my_shifts?${params.toString()}`
  } else if (notification.type == 'ShiftAdded' || notification.type == 'ShiftUpdated' || notification.type == 'ShiftRemoved') {
    // Use date range for shift notifications
    const startDate = notification.start_date || notification.shift_date || dayParam
    const endDate = notification.end_date || notification.shift_date || dayParam
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate.toString())
    if (endDate) params.append('end_date', endDate.toString())
    if (taskParam) params.append('task_id', taskParam.toString())
    return `/dashboard/my_shifts?${params.toString()}`
  }
  return '/dashboard'
}

function notification_message(notification: NotificationInfo) {
  const displayDay = notification.day || notification.shift_date || 0
  const firstName = notification.user_first_name || ''
  const lastName = notification.user_last_name || ''
  const userName = `${firstName} ${lastName}`.trim()
  const formattedDate = dayIdToLocaleString(displayDay)
  
  if (notification.type == 'TimeChanged') {
    return t('notifications.time_changed_body', { user_name: userName })
  } else if (notification.type == 'TimeApproved') {
    return t('notifications.time_approved_body', { date: formattedDate })
  } else if (notification.type == 'TimeUnapproved') {
    return t('notifications.time_unapproved_body', { date: formattedDate })
  } else if (notification.type == 'CommentChanged') {
    return t('notifications.comment_changed_body', { date: formattedDate, comment: notification.comment || '' })
  } else if (notification.type == 'ShiftAdded') {
    return t('notifications.shift_added_body', { date: formattedDate })
  } else if (notification.type == 'ShiftUpdated') {
    return t('notifications.shift_updated_body', { date: formattedDate })
  } else if (notification.type == 'ShiftRemoved') {
    return t('notifications.shift_removed_body', { date: formattedDate })
  }
  return t('notifications.new_notification', {}, 'New notification')
}

async function notification_clicked(notification: NotificationInfo) {
  // Mark as read using the new API
  if (!notification.is_read) {
    try {
      await markAsRead(notification.id, true)
      notification.is_read = true
      notification.read_at = Math.floor(Date.now() / 1000)
      
      // Update legacy visited state for backward compatibility
      visited(notification.id)
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }
  
  // Let the composable handle the state update
  isNotificationsSlideoverOpen.value = false
}

async function handleActionCompleted(_action: string, success: boolean) {
  if (success) {
    // Refresh the notifications list
    await refreshNotifications()
  }
}

function handleReadStatusChanged(notificationId: number, isRead: boolean) {
  // Update local notification state
  const notification = notifications.value.find(n => n.id === notificationId)
  if (notification) {
    notification.is_read = isRead
    if (isRead) {
      notification.read_at = Math.floor(Date.now() / 1000)
    } else {
      notification.read_at = undefined
    }
  }
  
  // Update hasNotification state using the visited method for legacy compatibility
  // hasNotification is readonly, so we'll let the composable handle the state update
}
</script>

<template>
  <UDashboardSlideover
    v-model="isNotificationsSlideoverOpen"
    title="Notifications"
  >
    <template #header>
      <div class="flex justify-between items-center w-full">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
          {{ $t('notifications.title') }}
        </h2>
        <UButton
          color="gray"
          variant="ghost"
          size="sm" 
          icon="i-heroicons-x-mark-20-solid"
          @click="isNotificationsSlideoverOpen = false"
        />
      </div>
    </template>
    
    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center py-8">
      <UIcon name="i-heroicons-arrow-path" class="w-6 h-6 animate-spin text-gray-500" />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="p-4">
      <UAlert color="red" variant="subtle">
        <template #title>{{ $t('notifications.error_loading') }}</template>
        <template #description>{{ error }}</template>
      </UAlert>
    </div>

    <!-- Empty state -->
    <div v-else-if="notifications.length === 0" class="text-center py-8">
      <UIcon name="i-heroicons-bell-slash" class="w-12 h-12 mx-auto text-gray-400 mb-4" />
      <p class="text-gray-500">{{ $t('notifications.no_notifications') }}</p>
    </div>

    <!-- Notifications list -->
    <div v-else>
      <div
        v-for="notification in notifications"
        :key="notification.id"
        class="p-4 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/50 relative"
      >
      <div class="grid grid-cols-[auto_1fr_auto] gap-3 items-start min-h-[64px]">
        <!-- Icon Column - Fixed Width -->
        <div class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 relative mt-1">
          <UIcon
            :name="getNotificationStyle(notification).icon"
            :class="`w-5 h-5 ${getNotificationStyle(notification).colorClass}`"
          />
          <!-- Unread indicator -->
          <div 
            v-if="!notification.is_read"
            class="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-800"
          ></div>
        </div>

        <!-- Content Column - Flexible Width -->
        <NuxtLinkLocale
          @click="notification_clicked(notification)"
          :to="get_link(notification)"
          class="text-sm cursor-pointer min-w-0 py-1"
        >
          <div class="space-y-1">
            <div class="flex items-start justify-between gap-2">
              <span class="text-gray-900 dark:text-white font-medium line-clamp-2 flex-1">{{ notification_message(notification) }}</span>
              <time
                :datetime="new Date(notification.create_date * 1000).toISOString()"
                class="text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap flex-shrink-0 mt-0.5"
                v-text="formatTimeAgo(new Date(notification.create_date * 1000))"
              />
            </div>
            <p class="text-gray-500 dark:text-gray-400 text-xs line-clamp-1">
              {{ notification.comment || notification.shift_info || '' }}
            </p>
          </div>
        </NuxtLinkLocale>

        <!-- Actions Column - Fixed Width -->
        <div class="w-auto flex justify-end mt-1">
          <NotificationActions
            :notification="notification"
            :is-read="notification.is_read"
            :show-view-button="false"
            @action-completed="handleActionCompleted"
            @read-status-changed="handleReadStatusChanged"
          />
        </div>
      </div>
    </div>
    </div>
  </UDashboardSlideover>
</template>
